/** The fields every tracked search carries. A page's own search type extends this with whatever
 *  that search collects while it runs. */
export interface TrackedSearchOperation {
  id: number;
  text: string;
}

/**
 * Keeps track of which search a search screen is currently running, so a search that a newer one
 * has overtaken can notice and throw its results away instead of publishing them over the newer
 * ones.
 *
 * One search is "current" at a time. Starting a search makes it current; a search asks
 * `shouldCancel` after every await and stops if it is no longer current; `end` releases the slot.
 * Starting a search for the same text as the one already running is refused, so a debouncer and an
 * explicit commit asking for the same text only run it once.
 *
 * With `profiling` on, every search logs when it starts, is cancelled and ends, and is timed with
 * `console.time`.
 */
export class SearchOperationTracker<TOp extends TrackedSearchOperation> {
  private nextSearchId = 0;
  private current: TOp = undefined;

  constructor(private readonly profiling = false) { }

  /** True while a search has the slot (started and neither ended nor superseded). */
  get isRunning(): boolean {
    return !!this.current;
  }

  /** Makes a new search current and returns it. Returns `undefined` if the operation shouldn't be
   *  started (e.g. same string is already running). */
  start(text: string, payload: Omit<TOp, keyof TrackedSearchOperation>): TOp | undefined {
    if (this.current && this.current.text === text) {
      return undefined;
    }

    let op = { id: this.nextSearchId++, text: text, ...payload } as TOp;
    if (this.profiling) {
      let label = this.label(op);
      console.log(`${label}: started`);
      console.time(label);
    }

    this.current = op;
    return op;
  }

  /** True when `op` has been overtaken and must stop without publishing anything. */
  shouldCancel(op: TOp): boolean {
    if (this.current !== op) {
      if (this.profiling) {
        let label = this.label(op);
        console.log(`${label}: cancelling`);
      }

      return true;
    }
    return false;
  }

  /** Same test as `shouldCancel`, inverted and without its "cancelling" profiling log -- for paths
   *  where the search was NOT cancelled, where logging one would mislead. */
  isCurrent(op: TOp): boolean {
    return this.current === op;
  }

  /**
   * Gives up the claim on the search that is running, without ending it, so that search counts as
   * superseded and is not allowed to publish its rows. Needed whenever something should overtake
   * the running search without starting a search of its own: an emptied search box, for instance,
   * or a caller that must re-run the same text. Without it nothing ever takes the slot and the
   * running search stays "current" all the way to its own last line.
   *
   * `end` is the wrong call here. That search has not ended -- it is still awaiting its work and
   * will end itself when that comes back -- and ending it twice would close its profiling timer
   * twice.
   *
   * Nothing is logged here even when profiling is on, deliberately: the search this gives up on
   * still reports itself, because `shouldCancel` logs 'cancelling' the moment it notices it was
   * overtaken.
   */
  supersede(): void {
    this.current = undefined;
  }

  /** Called once by every search that started, however it finishes. Releases the slot if `op`
   *  still holds it. */
  end(op: TOp): void {
    if (this.profiling) {
      let label = this.label(op);
      console.log(`${label}: ended${this.current !== op ? ' while cancelled' : ''}`);
      console.timeEnd(label);
    }
    if (this.current == op) {
      this.current = undefined;
    }
  }

  private label(op: TOp) {
    return `search #${op.id} - '${op.text}'`;
  }
}
